import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core';
import classNames from 'classnames';
import { useState, useEffect, useRef } from 'react';

import useStyles from '@components/common/faq/styles';
import { useGlobalResize, useGlobalScroll } from '@hooks/event-hooks';
import idNormalize from '@utils/id-normalize';

export interface QuestionProps {
  question: Question;
}

export interface Question {
  text: string | JSX.Element;
  title: string;
}

interface Props {
  info: Question[];
  stickyClassWrapper?: string;
}

const QuestionItem: React.FC<QuestionProps> = ({ question }) => {
  const [expanded, setExpanded] = useState(false);
  const classes = useStyles();
  const { title, text } = question;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      className={classes.question}
    >
      <AccordionSummary
        className={classes.summary}
        expandIcon={<i className="icon-arrow-bold" />}
      >
        <Typography className={classes.title} variant="body1">
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>{text}</AccordionDetails>
    </Accordion>
  );
};

const FaqDesktop: React.FC<Props> = (props) => {
  const { info, stickyClassWrapper } = props;
  const classes = useStyles();
  const titlesRef = useRef<Array<HTMLButtonElement | null>>([]);
  const answersRef = useRef<Array<HTMLDivElement | null>>([]);

  const handleScroll = () => {
    titlesRef?.current?.forEach((title, index) => {
      const section = answersRef?.current[index];

      if (title && section) {
        const sectionPosition = section.getBoundingClientRect();
        const isVisible =
          Math.abs(sectionPosition.top) < sectionPosition.height;

        if (sectionPosition.y < 30 && isVisible) {
          title.classList.add('active');
        } else {
          title.classList.remove('active');
        }
      }
    });
  };

  useGlobalScroll(handleScroll);

  return (
    <section className={classNames(classes.questionsWrapper)}>
      <div className={classes.leftColumn}>
        <div className={classNames(classes.sticky, stickyClassWrapper)}>
          {info.map((item, index) => (
            <button
              id={idNormalize(item.title)}
              type="button"
              ref={(el) => {
                titlesRef.current[index] = el;
              }}
              onClick={() => {
                answersRef?.current[index]?.scrollIntoView({
                  block: 'start',
                  behavior: 'smooth',
                });
              }}
              key={item.title}
              className={classes.questionButton}
            >
              <Typography variant="body2">{item.title}</Typography>
            </button>
          ))}
        </div>
      </div>
      <div className={classes.rightColumn}>
        {info.map((item, index) => (
          <div
            ref={(el) => {
              answersRef.current[index] = el;
            }}
            key={`${item.title}-answer`}
            className={classes.answer}
          >
            <Typography variant="subtitle1">{item.title}</Typography>
            {item.text}
          </div>
        ))}
      </div>
    </section>
  );
};

const FAQ: React.FC<Props> = (props) => {
  const { info, stickyClassWrapper } = props;
  const [windowWidth, setWindowWidth] = useState(0);

  useEffect(() => {
    if (!windowWidth) setWindowWidth(window.innerWidth);
  }, [windowWidth]);

  useGlobalResize(() => setWindowWidth(window.innerWidth));

  const classes = useStyles();

  if (windowWidth < 960) {
    const questions = info.map((question) => (
      <QuestionItem key={question.title} question={question} />
    ));

    return (
      <section className={classNames(classes.questionsWrapper)}>
        {questions}
      </section>
    );
  }

  return <FaqDesktop info={info} stickyClassWrapper={stickyClassWrapper} />;
};

export default FAQ;
